/**
 * Mobile Enhancements for Nor'East Apothecary
 * Handles mobile-specific UX improvements
 */

class MobileEnhancements {
  constructor() {
    this.headerWrapper = document.querySelector('.header-wrapper');
    this.headerElement = document.querySelector('.header.header--middle-center');
    this.menuDrawerContainer = document.querySelector('.menu-drawer-container');
    this.cartDrawer = document.querySelector('cart-drawer');

    this.init();
  }

  init() {
    // Fix transparent header when mobile menu opens
    this.initMenuDrawerListener();

    // Fix cart drawer close button on mobile
    this.initCartDrawerListener();

    // Fix header when cart opens
    this.initCartStateListener();

    // Ensure header stays solid when any drawer is open
    this.initDrawerStateObserver();
  }

  /**
   * When mobile menu opens, force header to solid white
   */
  initMenuDrawerListener() {
    if (!this.menuDrawerContainer) return;

    // Use MutationObserver to watch for open attribute changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          const isOpen = this.menuDrawerContainer.hasAttribute('open');
          this.setHeaderSolidState(isOpen);
        }
      });
    });

    observer.observe(this.menuDrawerContainer, {
      attributes: true,
      attributeFilter: ['open']
    });

    // Also listen for toggle event
    this.menuDrawerContainer.addEventListener('toggle', () => {
      const isOpen = this.menuDrawerContainer.hasAttribute('open');
      this.setHeaderSolidState(isOpen);
    });
  }

  /**
   * Improve cart drawer close button on mobile
   */
  initCartDrawerListener() {
    if (!this.cartDrawer) return;

    // Ensure close button is always visible and accessible on mobile
    const cartDrawerInner = this.cartDrawer.querySelector('.drawer__inner');
    if (!cartDrawerInner) return;

    // Make sure the drawer overlay is clickable to close
    const overlay = document.querySelector('.cart-drawer__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => {
        this.cartDrawer.classList.remove('active');
        this.cartDrawer.classList.remove('is-empty');
        document.body.classList.remove('overflow-hidden');
      });
    }
  }

  /**
   * Watch for cart drawer open/close events
   */
  initCartStateListener() {
    // Watch for cart drawer class changes
    const cartDrawerObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isActive = this.cartDrawer.classList.contains('active');
          this.setHeaderSolidState(isActive);
        }
      });
    });

    if (this.cartDrawer) {
      cartDrawerObserver.observe(this.cartDrawer, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  /**
   * General drawer state observer for any drawer elements
   */
  initDrawerStateObserver() {
    // Watch body for overflow-hidden class (added when drawers open)
    const bodyObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const hasOverflowHidden = document.body.classList.contains('overflow-hidden');
          this.setHeaderSolidState(hasOverflowHidden);
        }
      });
    });

    bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /**
   * Set header to solid white or restore transparent state
   */
  setHeaderSolidState(isSolid) {
    if (!this.headerWrapper || !this.headerElement) return;

    if (isSolid) {
      // Add class to force solid header
      this.headerWrapper.classList.add('drawer-open');
      this.headerElement.classList.add('drawer-open');

      // Force inline styles for immediate effect
      this.headerWrapper.style.setProperty('background-color', '#fff', 'important');
      this.headerWrapper.style.setProperty('border-bottom', '1px solid rgba(0, 0, 0, 0.06)', 'important');

      // Reset text colors to dark
      const textElements = this.headerElement.querySelectorAll('.header__menu-item, .header__heading-link .h2, .header__icon');
      textElements.forEach(el => {
        el.style.setProperty('color', '#1a1a1a', 'important');
      });

      // Reset SVG strokes
      const svgElements = this.headerElement.querySelectorAll('.header__icon svg, .icon-caret');
      svgElements.forEach(el => {
        el.style.setProperty('stroke', '#1a1a1a', 'important');
      });

      // Reset logo filter
      const logo = this.headerElement.querySelector('.header__heading-logo');
      if (logo) {
        logo.style.setProperty('filter', 'none', 'important');
      }
    } else {
      // Remove solid state, restore to default (may be transparent)
      this.headerWrapper.classList.remove('drawer-open');
      this.headerElement.classList.remove('drawer-open');

      // Remove inline styles
      this.headerWrapper.style.removeProperty('background-color');
      this.headerWrapper.style.removeProperty('border-bottom');

      // Remove inline styles from text elements
      const textElements = this.headerElement.querySelectorAll('.header__menu-item, .header__heading-link .h2, .header__icon');
      textElements.forEach(el => {
        el.style.removeProperty('color');
      });

      // Remove inline styles from SVG elements
      const svgElements = this.headerElement.querySelectorAll('.header__icon svg, .icon-caret');
      svgElements.forEach(el => {
        el.style.removeProperty('stroke');
      });

      // Remove logo filter
      const logo = this.headerElement.querySelector('.header__heading-logo');
      if (logo) {
        logo.style.removeProperty('filter');
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MobileEnhancements();
  });
} else {
  new MobileEnhancements();
}
